/**
 * useTopicMutations — centralized write-side hook for Topic ops.
 *
 * Strategy (matches Step 1 design §2.4):
 *  - Lazy-load topics on first mount of the consumer
 *  - All writes use a "call client → on success reload list()" cycle
 *    (no optimistic updates; data volume is small)
 *  - Per-Topic lock prevents concurrent writes on the same card
 *  - Errors surface via `mutationError` state, auto-cleared on next success, AND are
 *    returned to the caller so a hosting modal can show the same message instead of
 *    its own generic retry advice (all Topic ops are write-gated: a 403 is not retryable)
 */

import { useCallback, useEffect, useState } from 'react';
import type { Topic } from '../../../client/IvtHubClient';
import {
  topicClient,
  type UpsertTopicInput,
  type UpsertTopicInstanceInput,
  type DeleteTopicInstanceInput,
} from '../../../client/topicClient';
import { denialHint } from '../../../utils/authorization';

interface UseTopicMutationsState {
  topics: Topic[] | null;
  loading: boolean;
  loadError: string | null;
  mutationError: string | null;
  locked: Set<string>;
}

export function useTopicMutations(enabled: boolean) {
  const [state, setState] = useState<UseTopicMutationsState>({
    topics: null,
    loading: false,
    loadError: null,
    mutationError: null,
    locked: new Set(),
  });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, loadError: null }));
    try {
      const { topics } = await topicClient.list();
      setState((s) => ({ ...s, topics, loading: false }));
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        loadError: (e as Error).message ?? '加载失败',
      }));
    }
  }, []);

  useEffect(() => {
    // Guard MUST include loadError — otherwise a failed reload() leaves
    // loading=false + topics=null + loadError set, and the effect would re-fire
    // immediately in an infinite request loop. Consumers can call `reload()`
    // manually to retry after surfacing the error to the user.
    if (!enabled || state.topics !== null || state.loading || state.loadError) return;
    reload();
  }, [enabled, state.topics, state.loading, state.loadError, reload]);

  const dismissMutationError = useCallback(() => {
    setState((s) => ({ ...s, mutationError: null }));
  }, []);

  const runMutation = useCallback(
    async (topicName: string, fn: () => Promise<void>): Promise<string | null> => {
      let alreadyLocked = false;
      setState((s) => {
        if (s.locked.has(topicName)) {
          alreadyLocked = true;
          return s;
        }
        const locked = new Set(s.locked);
        locked.add(topicName);
        return { ...s, locked, mutationError: null };
      });
      // A UI guard, not a failure to report: the card's controls are disabled while
      // its lock is held.
      if (alreadyLocked) return null;

      try {
        await fn();
        const { topics } = await topicClient.list();
        setState((s) => {
          const locked = new Set(s.locked);
          locked.delete(topicName);
          return { ...s, topics, locked };
        });
        return null;
      } catch (e) {
        // Action first, server text as a parenthetical diagnostic — see the matching
        // comment in useSolutionMapMutations.runUnderLock. Returning the raw message
        // alone put English SDK text in the Topic modals.
        const raw = (e as Error).message;
        const message =
          denialHint(e) ?? (raw ? `操作失败，请重试。（${raw}）` : '操作失败，请重试。');
        setState((s) => {
          const locked = new Set(s.locked);
          locked.delete(topicName);
          return { ...s, locked, mutationError: message };
        });
        return message;
      }
    },
    [],
  );

  const upsertTopic = useCallback(
    (input: UpsertTopicInput) =>
      runMutation(input.topicName, async () => {
        await topicClient.upsert(input);
      }),
    [runMutation],
  );

  const deleteTopic = useCallback(
    (topicName: string) =>
      runMutation(topicName, async () => {
        await topicClient.deleteTopic({ topicName });
      }),
    [runMutation],
  );

  const upsertInstance = useCallback(
    (input: UpsertTopicInstanceInput) =>
      runMutation(input.topicName, async () => {
        await topicClient.upsertInstance(input);
      }),
    [runMutation],
  );

  const deleteInstance = useCallback(
    (input: DeleteTopicInstanceInput) =>
      runMutation(input.topicName, async () => {
        await topicClient.deleteInstance(input);
      }),
    [runMutation],
  );

  return {
    topics: state.topics,
    loading: state.loading,
    loadError: state.loadError,
    mutationError: state.mutationError,
    isLocked: (topicName: string) => state.locked.has(topicName),
    reload,
    dismissMutationError,
    upsertTopic,
    deleteTopic,
    upsertInstance,
    deleteInstance,
  };
}
